import { getAppTitle, getAppDescription } from '../utils/config';
import { keywordsZh, keywordsEn } from './keywords';

export type Locale = 'zh' | 'en';

export interface SeoMeta {
  title: string;
  description: string;
  keywords: string[];
  path: string;
  alternatePath?: string;
  locale: Locale;
  noindex?: boolean;
}

const brand = () => getAppTitle();

export const seoPages: Record<string, SeoMeta> = {
  '/': {
    title: `${brand()} - 专业跨境网络服务`,
    description:
      '一键连提供稳定可靠的跨境网络服务，助力跨境电商、TikTok运营、海外AI工具（ChatGPT/Claude）及国际协作，支持 Windows、macOS、Android 全平台 NodeUnion 官方客户端。',
    keywords: [...keywordsZh.home],
    path: '/',
    alternatePath: '/en',
    locale: 'zh',
  },
  '/pricing': {
    title: `跨境网络套餐价格 - ${brand()} 企业订阅计划`,
    description:
      '查看一键连跨境网络套餐价格，灵活月付/季付/年付，支持 TikTok 业务节点、海外 AI 工具线路与多设备，登录后查看实时价格并购买。',
    keywords: [...keywordsZh.pricing],
    path: '/pricing',
    alternatePath: '/en/pricing',
    locale: 'zh',
  },
  '/features': {
    title: `跨境网络功能 - TikTok运营与海外AI工具 | ${brand()}`,
    description:
      '一键连跨境网络提供企业级加密、全球业务节点、TikTok 运营网络、海外 AI 工具访问优化、跨境电商支持与多设备在线，满足出海业务需求。',
    keywords: [...keywordsZh.features],
    path: '/features',
    alternatePath: '/en/features',
    locale: 'zh',
  },
  '/download': {
    title: `NodeUnion 客户端下载 - 跨境业务客户端 | ${brand()}`,
    description:
      '下载 NodeUnion 官方 Windows、macOS、Android 跨境网络客户端，登录账户即可同步节点，快速开启跨境电商与 TikTok 运营业务。',
    keywords: [...keywordsZh.download],
    path: '/download',
    alternatePath: '/en/download',
    locale: 'zh',
  },
  '/download/windows': {
    title: `Windows 跨境客户端下载与配置 | ${brand()}`,
    description:
      'Windows 10/11 下载 NodeUnion 官方客户端，登录后自动同步跨境网络节点，适用于跨境电商、TikTok 运营与海外 AI 工具访问。',
    keywords: [...keywordsZh.downloadWindows],
    path: '/download/windows',
    alternatePath: '/en/download/windows',
    locale: 'zh',
  },
  '/download/macos': {
    title: `Mac 跨境客户端 - macOS 全球业务网络 | ${brand()}`,
    description:
      'Apple Silicon Mac 下载 NodeUnion 官方客户端，三步完成配置，稳定支撑 TikTok 运营、Shopify 管理与海外 AI 工具使用。',
    keywords: [...keywordsZh.downloadMacos],
    path: '/download/macos',
    alternatePath: '/en/download/macos',
    locale: 'zh',
  },
  '/download/android': {
    title: `Android 跨境客户端下载 | ${brand()}`,
    description:
      'Android 手机下载 NodeUnion 官方 APK，登录同步跨境网络节点，适合 TikTok 移动端运营与外出办公场景。',
    keywords: [...keywordsZh.downloadAndroid],
    path: '/download/android',
    alternatePath: '/en/download/android',
    locale: 'zh',
  },
  '/download/ios': {
    title: `iOS 跨境网络配置指南 | ${brand()}`,
    description:
      'iPhone/iPad 跨境网络服务说明，登录用户中心查看详细配置教程，支持移动端 TikTok 运营与海外业务访问。',
    keywords: [...keywordsZh.downloadIos],
    path: '/download/ios',
    alternatePath: '/en/download/ios',
    locale: 'zh',
  },
  '/help': {
    title: `跨境网络使用指南与常见问题 | ${brand()}`,
    description:
      '一键连帮助中心：NodeUnion 客户端配置、TikTok 运营网络建议、海外 AI 工具访问优化、跨境电商常见问题与故障排查。',
    keywords: [...keywordsZh.help],
    path: '/help',
    alternatePath: '/en/help',
    locale: 'zh',
  },
  '/about': {
    title: `关于我们 - ${brand()} 跨境网络服务`,
    description:
      '了解一键连品牌故事与服务承诺，专注为跨境电商、TikTok 运营者与出海企业提供安全稳定的全球业务网络。',
    keywords: [...keywordsZh.about],
    path: '/about',
    alternatePath: '/en/about',
    locale: 'zh',
  },
  '/privacy': {
    title: `隐私政策 - ${brand()} 数据保护`,
    description:
      '一键连隐私政策：说明信息收集与使用方式，承诺无日志政策，保护用户跨境业务数据与隐私安全。',
    keywords: [...keywordsZh.privacy],
    path: '/privacy',
    alternatePath: '/en/privacy',
    locale: 'zh',
  },
  '/terms': {
    title: `服务条款 - ${brand()} 跨境网络使用规范`,
    description:
      '一键连服务条款与使用规范，包含账户规则、跨境网络服务范围、退款说明及用户责任，使用前请仔细阅读。',
    keywords: [...keywordsZh.terms],
    path: '/terms',
    alternatePath: '/en/terms',
    locale: 'zh',
  },
  '/login': {
    title: `登录 - ${brand()} 用户中心`,
    description: `登录 ${brand()} 账户，管理跨境网络订阅、查看流量与节点状态。`,
    keywords: [brand(), '登录', '用户中心'],
    path: '/login',
    alternatePath: '/en/login',
    locale: 'zh',
    noindex: true,
  },
  '/register': {
    title: `注册 - ${brand()} 跨境网络服务`,
    description: `注册 ${brand()} 账户，立即体验专业跨境网络服务，支持跨境电商、TikTok 运营与海外 AI 工具场景。`,
    keywords: [brand(), '注册', '跨境网络', '跨境电商'],
    path: '/register',
    alternatePath: '/en/register',
    locale: 'zh',
    noindex: true,
  },
  '/en': {
    title: `${brand()} - Cross-Border Network Service`,
    description:
      'OneTapLink provides reliable cross-border network services for e-commerce, TikTok operations, overseas AI tools, and global business collaboration.',
    keywords: [...keywordsEn.home],
    path: '/en',
    alternatePath: '/',
    locale: 'en',
  },
  '/en/pricing': {
    title: `Cross-Border Network Pricing | ${brand()}`,
    description:
      'Explore OneTapLink cross-border network plans with TikTok and AI-optimized routes. Sign in for live pricing.',
    keywords: [...keywordsEn.pricing],
    path: '/en/pricing',
    alternatePath: '/pricing',
    locale: 'en',
  },
  '/en/features': {
    title: `Cross-Border Features - TikTok & AI Tools | ${brand()}`,
    description:
      'Enterprise encryption, global business nodes, TikTok operations network, overseas AI access, and e-commerce support.',
    keywords: [...keywordsEn.features],
    path: '/en/features',
    alternatePath: '/features',
    locale: 'en',
  },
  '/en/download': {
    title: `NodeUnion Client Download | ${brand()}`,
    description:
      'Download the official NodeUnion client for Windows, macOS, and Android. Sign in to sync your cross-border network.',
    keywords: [...keywordsEn.download],
    path: '/en/download',
    alternatePath: '/download',
    locale: 'en',
  },
  '/en/download/windows': {
    title: `Windows Cross-Border Client Setup | ${brand()}`,
    description:
      'Install NodeUnion on Windows for cross-border e-commerce, TikTok operations, and overseas AI tool access.',
    keywords: [...keywordsEn.downloadWindows],
    path: '/en/download/windows',
    alternatePath: '/download/windows',
    locale: 'en',
  },
  '/en/download/macos': {
    title: `Mac Cross-Border Client Setup | ${brand()}`,
    description:
      'Configure NodeUnion on Apple Silicon Mac for TikTok ops, Shopify management, and global business networking.',
    keywords: [...keywordsEn.downloadMacos],
    path: '/en/download/macos',
    alternatePath: '/download/macos',
    locale: 'en',
  },
  '/en/download/android': {
    title: `Android Cross-Border Client Setup | ${brand()}`,
    description:
      'Download NodeUnion APK on Android for mobile TikTok operations and cross-border business on the go.',
    keywords: [...keywordsEn.downloadAndroid],
    path: '/en/download/android',
    alternatePath: '/download/android',
    locale: 'en',
  },
  '/en/download/ios': {
    title: `iOS Cross-Border Network Guide | ${brand()}`,
    description:
      'iOS cross-border network setup guide. Sign in to the user center for detailed configuration tutorials.',
    keywords: [...keywordsEn.downloadIos],
    path: '/en/download/ios',
    alternatePath: '/download/ios',
    locale: 'en',
  },
  '/en/help': {
    title: `Cross-Border Network Guide & FAQ | ${brand()}`,
    description:
      'Help center for NodeUnion setup, TikTok operations, overseas AI tools, cross-border e-commerce, and troubleshooting.',
    keywords: [...keywordsEn.help],
    path: '/en/help',
    alternatePath: '/help',
    locale: 'en',
  },
  '/en/about': {
    title: `About Us - ${brand()} Cross-Border Network`,
    description:
      'Learn about OneTapLink and our commitment to secure cross-border networking for global businesses.',
    keywords: [...keywordsEn.about],
    path: '/en/about',
    alternatePath: '/about',
    locale: 'en',
  },
  '/en/privacy': {
    title: `Privacy Policy - ${brand()}`,
    description: 'OneTapLink privacy policy covering data collection, usage, protection, and no-logs commitment.',
    keywords: [...keywordsEn.privacy],
    path: '/en/privacy',
    alternatePath: '/privacy',
    locale: 'en',
  },
  '/en/terms': {
    title: `Terms of Service - ${brand()}`,
    description: 'OneTapLink terms covering account rules, service scope, refunds, and user responsibilities.',
    keywords: [...keywordsEn.terms],
    path: '/en/terms',
    alternatePath: '/terms',
    locale: 'en',
  },
  '/en/login': {
    title: `Login - ${brand()}`,
    description: `Sign in to your ${brand()} account to manage cross-border network subscriptions.`,
    keywords: [brand(), 'login'],
    path: '/en/login',
    alternatePath: '/login',
    locale: 'en',
    noindex: true,
  },
  '/en/register': {
    title: `Register - ${brand()}`,
    description: `Create a ${brand()} account for cross-border network services today.`,
    keywords: [brand(), 'register', 'cross-border network'],
    path: '/en/register',
    alternatePath: '/register',
    locale: 'en',
    noindex: true,
  },
};

export function getSeoForPath(pathname: string): SeoMeta {
  const normalized = pathname.endsWith('/') && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;
  if (normalized === '/landing') return seoPages['/'];
  return seoPages[normalized] || seoPages['/'];
}

export function getLocaleFromPath(pathname: string): Locale {
  return pathname.startsWith('/en') ? 'en' : 'zh';
}

export function localizePath(path: string, locale: Locale): string {
  if (locale === 'en') {
    if (path === '/') return '/en';
    return path.startsWith('/en') ? path : `/en${path}`;
  }
  if (path === '/en') return '/';
  return path.startsWith('/en') ? path.replace(/^\/en/, '') || '/' : path;
}
